import { useEffect } from 'react';
import { ChevronsLeft, ChevronsRight } from '../../icons';
import { NavBarBrand } from './NavBarBrand';
import { useAuthStore } from '../../store/authStore';
import { useSidebarStore } from '../../store/sidebarStore';
import { useWorkStatusStore } from '../../store/workStatusStore';
import { useProfileUiStore } from '../../store/profileUiStore';
import { statusLabel } from '../StatusDropdown';
import { WORK_STATUS_TONE } from '../../lib/work-status';
import { tLayout } from '../../lib/layout-i18n';

/**
 * ① 레일 머리 — 정본 `axi-components.html` **H1-4**.
 *
 * 대시보드 위에 선다. **브랜드 한 줄 + (토글 + 사용자 줄) 한 줄.**
 * 접히면 사용자 줄이 **통째로 비고** 토글만 남는다.
 *
 * ★★ **머리 높이는 두 상태에서 같아야 한다**(`--rail-head-h`).
 *    다르면 토글을 누를 때마다 아래 메뉴 목록이 통째로 위아래로 뛰어, 방금 누른 자리에
 *    다른 메뉴가 와 있게 된다. 시안 단계에서 접힘 머리에 «로고·토글·아바타» 셋을 세웠다가
 *    펼침보다 44px 높아 실제로 뛰는 것을 화면에서 확인하고 고친 값이다.
 *
 * ★ **아바타를 두지 않는다**(2026-09-07 사용자 지시). 이름·근무 상태·출근~퇴근 시각만 적는다.
 *   얼굴 그림은 ⑤ 우측 레일에 그대로 있으므로 좌측은 «글자», 우측은 «그림» 으로 갈린다 —
 *   같은 것이 두 번 보이지 않고, 접혀 있는 동안 프로필로 들어갈 문도 우측에 하나 남는다.
 *
 * ★ 근무 상태는 ⑥ 상태바와 **같은 값**(`workStatusStore`)을 본다. 다만 **바꾸는 자리는
 *   상태바 하나**다 — 여기는 읽기 전용 표시다.
 *   누르면 **설정 > 내 정보 관리 > 프로필**이 열린다(2026-09-07). 종전에는 `/me`(내 프로필
 *   화면)로 갔는데 그 화면을 없앴다 — 설정과 내용이 겹쳤다.
 */
export function RailHead() {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  const label = collapsed ? tLayout('layout.rail.expand') : tLayout('layout.rail.collapse');

  return (
    <div className="rail-head">
      <NavBarBrand />
      <div className="rail-head__row">
        <button
          type="button"
          className="rail-toggle"
          aria-expanded={!collapsed}
          aria-label={label}
          title={label}
          onClick={toggle}
        >
          {collapsed ? <ChevronsRight size={20} aria-hidden /> : <ChevronsLeft size={20} aria-hidden />}
        </button>
        {!collapsed && <RailUserLine />}
      </div>
    </div>
  );
}

/**
 * 사용자 줄 — 이름 · 근무 상태 · 출근~퇴근 시각. **펼쳤을 때만** 그린다.
 *
 * 시각은 새 API 를 부르지 않는다 — ⑥ 상태바가 이미 읽어 둔 `workStatusStore.today`
 * (`HH:MM` 표시 문자열)를 그대로 본다. 레일이 따로 부르면 화면을 옮길 때마다 요청이 는다.
 */
function RailUserLine() {
  const user = useAuthStore((s) => s.user);
  const status = useWorkStatusStore((s) => s.status);
  const today = useWorkStatusStore((s) => s.today);
  const hydrated = useWorkStatusStore((s) => s.hydrated);
  const hydrate = useWorkStatusStore((s) => s.hydrate);
  const openSettings = useProfileUiStore((s) => s.openSettings);

  // 상태바가 이미 부르지만, 상태바가 없는 화면에서도 시각이 비지 않게 한 번 더 확인한다
  // (`hydrated` 가 참이면 아무 일도 하지 않으므로 요청이 늘지 않는다).
  useEffect(() => { if (!hydrated) void hydrate(); }, [hydrated, hydrate]);

  const tone = status ? WORK_STATUS_TONE[status] : 'none';
  const name = user?.name ?? '';
  const label = status ? statusLabel(status) : tLayout('layout.workstatus.unset');

  return (
    <button
      type="button"
      className="rail-me"
      onClick={() => openSettings('account', 'profile')}
      aria-label={`${name} — ${label}`}
    >
      <span className="rail-me__row">
        <b className="rail-me__name">{name}</b>
        <span className="rail-me__status" data-tone={tone}>{label}</span>
      </span>
      <span className="rail-me__time">{workTime(today)}</span>
    </button>
  );
}

/**
 * 출근~퇴근 표기 — 정본 F18.
 *
 *   출근 전 : 「출근 전」  (`— ~ —` 는 읽히지 않는다)
 *   근무 중 : `09:02 ~ —` (**없는 값은 —** 다 — 「18:00 예정」 같은 것을 지어내지 않는다)
 *   퇴근 후 : `09:02 ~ 18:12`
 */
function workTime(today: { checkIn: string | null; checkOut: string | null }) {
  if (!today.checkIn) return <i>{tLayout('layout.rail.before_checkin')}</i>;
  if (!today.checkOut) return <>{today.checkIn} <i>~ —</i></>;
  return <>{today.checkIn} ~ {today.checkOut}</>;
}
